<template>
  <div class="companies-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-content">
        <div class="hero-text">
          <h1 class="hero-title">{{ $t("website.companies.hero_title") }}</h1>
          <h2 class="hero-subtitle">
            {{ $t("website.companies.hero_subtitle") }}
          </h2>
          <p class="hero-description">
            {{ $t("website.companies.hero_description") }}
          </p>
          <div class="hero-cta">
            <NuxtLink to="/contact" class="btn btn-primary btn-large">{{
              $t("website.contact_us")
            }}</NuxtLink>
          </div>
        </div>
        <div class="hero-image">
          <div class="image-wrapper">
            <img
              src="@/assets/website/features/dashboard.png"
              title="ChronoFox Dashboard"
              alt="Dashboard mit linker Navigation, Umsatzübersicht, Kundenliste, Aktivitätenübersicht und digitaler Uhr"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Pain Points Section -->
    <section class="pain-points-section">
      <div class="pain-points-content">
        <h2 class="section-title gradient-text">
          {{ $t("website.companies.pain_points_title") }}
        </h2>
        <div class="title-underline"></div>
        <p class="section-subtitle">
          {{ $t("website.companies.pain_points_subtitle") }}
        </p>

        <div class="pain-points-grid">
          <div class="pain-point-card">
            <div class="pain-point-icon">
              <i class="fa fa-puzzle-piece"></i>
            </div>
            <h3>{{ $t("website.companies.pain_point_1_title") }}</h3>
            <p>{{ $t("website.companies.pain_point_1_description") }}</p>
          </div>
          <div class="pain-point-card">
            <div class="pain-point-icon">
              <i class="fa fa-chart-line"></i>
            </div>
            <h3>{{ $t("website.companies.pain_point_2_title") }}</h3>
            <p>{{ $t("website.companies.pain_point_2_description") }}</p>
          </div>
          <div class="pain-point-card">
            <div class="pain-point-icon">
              <i class="fa fa-file-contract"></i>
            </div>
            <h3>{{ $t("website.companies.pain_point_3_title") }}</h3>
            <p>{{ $t("website.companies.pain_point_3_description") }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Solution Section -->
    <section class="solution-section">
      <div class="solution-content">
        <h2 class="section-title gradient-text">
          {{ $t("website.companies.solution_title") }}
        </h2>
        <div class="title-underline"></div>
        <p class="section-subtitle">
          {{ $t("website.companies.solution_subtitle") }}
        </p>

        <div class="solution-grid">
          <div class="solution-feature">
            <div class="solution-icon">
              <i class="fa fa-tasks"></i>
            </div>
            <h3>{{ $t("website.companies.solution_feature_2_title") }}</h3>
            <p>{{ $t("website.companies.solution_feature_2_description") }}</p>
          </div>
          <div class="solution-feature">
            <div class="solution-icon">
              <i class="fa fa-chart-bar"></i>
            </div>
            <h3>{{ $t("website.companies.solution_feature_3_title") }}</h3>
            <p>{{ $t("website.companies.solution_feature_3_description") }}</p>
          </div>
          <div class="solution-feature">
            <div class="solution-icon">
              <i class="fa fa-users"></i>
            </div>
            <h3>{{ $t("website.companies.solution_feature_1_title") }}</h3>
            <p>{{ $t("website.companies.solution_feature_1_description") }}</p>
          </div>
          <div class="solution-feature">
            <div class="solution-icon">
              <i class="fa fa-user-cog"></i>
            </div>
            <h3>{{ $t("website.companies.solution_feature_4_title") }}</h3>
            <p>{{ $t("website.companies.solution_feature_4_description") }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Detailed Features Section -->
    <section class="detailed-features">
      <div class="detailed-features-content">
        <h2 class="section-title gradient-text">
          {{ $t("website.companies.detailed_features_title") }}
        </h2>
        <div class="title-underline"></div>
        <p class="section-description">
          {{ $t("website.companies.detailed_features_subtitle") }}
        </p>

        <div class="detailed-feature-cards">
          <div class="detailed-feature">
            <div class="feature-number">
              {{ $t("website.companies.feature_1_number") }}
            </div>
            <div class="feature-content">
              <h3>{{ $t("website.companies.feature_1_title") }}</h3>
              <p>{{ $t("website.companies.feature_1_description") }}</p>
            </div>
            <div class="feature-image">
              <img
                src="@/assets/website/features/dashboard.png"
                alt="Team Dashboard"
              />
            </div>
          </div>

          <div class="detailed-feature reverse">
            <div class="feature-number">
              {{ $t("website.companies.feature_2_number") }}
            </div>
            <div class="feature-content">
              <h3>{{ $t("website.companies.feature_2_title") }}</h3>
              <p>{{ $t("website.companies.feature_2_description") }}</p>
            </div>
            <div class="feature-image">
              <img
                src="@/assets/website/features/projects.png"
                alt="Project Management"
              />
            </div>
          </div>

          <div class="detailed-feature">
            <div class="feature-number">
              {{ $t("website.companies.feature_3_number") }}
            </div>
            <div class="feature-content">
              <h3>{{ $t("website.companies.feature_3_title") }}</h3>
              <p>{{ $t("website.companies.feature_3_description") }}</p>
            </div>
            <div class="feature-image">
              <img
                src="@/assets/website/features/manageable-customers.png"
                alt="Client & Billing Management"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="benefits-section">
      <div class="benefits-content">
        <h2 class="section-title gradient-text">
          {{ $t("website.companies.benefits_title") }}
        </h2>
        <div class="title-underline"></div>

        <div class="benefits-grid">
          <div class="benefit-card">
            <div class="benefit-icon">
              <i class="fa fa-rocket"></i>
            </div>
            <h3>{{ $t("website.companies.benefit_1_title") }}</h3>
            <p>{{ $t("website.companies.benefit_1_description") }}</p>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">
              <i class="fa fa-dollar-sign"></i>
            </div>
            <h3>{{ $t("website.companies.benefit_2_title") }}</h3>
            <p>{{ $t("website.companies.benefit_2_description") }}</p>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">
              <i class="fa fa-brain"></i>
            </div>
            <h3>{{ $t("website.companies.benefit_3_title") }}</h3>
            <p>{{ $t("website.companies.benefit_3_description") }}</p>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">
              <i class="fa fa-shield-alt"></i>
            </div>
            <h3>{{ $t("website.companies.benefit_4_title") }}</h3>
            <p>{{ $t("website.companies.benefit_4_description") }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="use-cases-section">
      <div class="use-cases-content">
        <h2 class="section-title gradient-text">
          {{ $t("website.companies.use_cases_title") }}
        </h2>
        <div class="title-underline"></div>

        <div class="use-cases-grid">
          <div class="use-case-card">
            <div class="use-case-icon">
              <i class="fa fa-palette"></i>
            </div>
            <h3>{{ $t("website.companies.use_case_1_title") }}</h3>
            <p>{{ $t("website.companies.use_case_1_description") }}</p>
          </div>
          <div class="use-case-card">
            <div class="use-case-icon">
              <i class="fa fa-code"></i>
            </div>
            <h3>{{ $t("website.companies.use_case_2_title") }}</h3>
            <p>{{ $t("website.companies.use_case_2_description") }}</p>
          </div>
          <div class="use-case-card">
            <div class="use-case-icon">
              <i class="fa fa-briefcase"></i>
            </div>
            <h3>{{ $t("website.companies.use_case_3_title") }}</h3>
            <p>{{ $t("website.companies.use_case_3_description") }}</p>
          </div>
          <div class="use-case-card">
            <div class="use-case-icon">
              <i class="fa fa-hands-helping"></i>
            </div>
            <h3>{{ $t("website.companies.use_case_4_title") }}</h3>
            <p>{{ $t("website.companies.use_case_4_description") }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Overview Section -->
    <section class="features-overview">
      <h2 class="section-title gradient-text">
        {{ $t("website.key_features") }}
      </h2>
      <div class="title-underline"></div>
      <p class="section-subtitle">
        {{ $t("website.key_features_subtitle") }}
      </p>
      <PagesFeaturesOverview :highlightFirst="true" />
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="pricing-section">
      <h2 class="section-title gradient-text">
        {{ $t("website.choose_your_plan") }}
      </h2>
      <div class="title-underline"></div>
      <p class="section-subtitle">
        {{ $t("website.choose_the_perfect_plan") }}
      </p>
      <PagesPricingCompanies />
    </section>

    <!-- Enterprise Section -->
    <section class="enterprise-section">
      <div class="enterprise-content">
        <div class="enterprise-text">
          <h2>
            {{ $t("website.companies.enterprise_title") }}
          </h2>
          <p>{{ $t("website.companies.enterprise_description") }}</p>

          <ul class="enterprise-features">
            <li>
              <i class="fa fa-check-circle"></i>
              {{ $t("website.companies.enterprise_feature_1") }}
            </li>
            <li>
              <i class="fa fa-check-circle"></i>
              {{ $t("website.companies.enterprise_feature_2") }}
            </li>
            <li>
              <i class="fa fa-check-circle"></i>
              {{ $t("website.companies.enterprise_feature_3") }}
            </li>
            <li>
              <i class="fa fa-check-circle"></i>
              {{ $t("website.companies.enterprise_feature_4") }}
            </li>
            <li>
              <i class="fa fa-check-circle"></i>
              {{ $t("website.companies.enterprise_feature_5") }}
            </li>
          </ul>

          <NuxtLink to="/contact" class="btn btn-primary btn-large">{{
            $t("website.companies.enterprise_cta")
          }}</NuxtLink>
        </div>
      </div>
    </section>

    <!-- Trust Section -->
    <section class="trust-section">
      <h2 class="section-title gradient-text">
        {{ $t("website.companies.trust_title") }}
      </h2>
      <div class="title-underline"></div>
      <p class="section-subtitle">
        {{ $t("website.companies.trust_description") }}
      </p>

      <ProfessionsMarquee />

      <div class="trust-features">
        <div class="trust-item">
          <i class="fa fa-clock"></i>
          <h4>{{ $t("website.easy_start") }}</h4>
          <p>{{ $t("website.ready_in_minutes") }}</p>
        </div>
        <div class="trust-item">
          <i class="fa fa-headset"></i>
          <h4>{{ $t("website.german_support") }}</h4>
          <p>{{ $t("website.help_in_german") }}</p>
        </div>
        <div class="trust-item">
          <i class="fa fa-shield-alt"></i>
          <h4>{{ $t("website.gdpr_compliant") }}</h4>
          <p>{{ $t("website.your_data_is_safe") }}</p>
        </div>
      </div>
    </section>

    <!-- Final CTA Section -->
    <section class="final-cta">
      <h2>{{ $t("website.companies.final_cta_title") }}</h2>
      <p>{{ $t("website.companies.final_cta_description") }}</p>
      <div class="cta-buttons">
        <NuxtLink to="/contact" class="btn btn-primary btn-large">
          {{ $t("website.contact_us") }}
        </NuxtLink>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: "website",
});

const { t, locale } = useI18n();

import logo from "@/assets/logo/chronofox_wortbild_right.png";
useSeoMeta({
  // General
  title: t("website.companies.hero_title") + " | " + t("app_name"),
  description: t("website.companies.hero_description"),
  keywords: `${t("time_tracking")}, ${t("companies")}, ${t("team_collaboration")}, ${t("project_management")}, ${t("advanced_reporting")}`,

  // Open Graph
  ogTitle: t("app_name") + " - " + t("website.companies.hero_title"),
  ogDescription: t("website.companies.hero_description"),
  ogLocale: locale.value ? locale.value : "de",
  ogType: "website",
  ogImage: logo,

  // Twitter (X)
  twitterCard: "summary_large_image",
  twitterTitle: t("website.companies.hero_title") + " | " + t("app_name"),
  twitterDescription: t("website.companies.hero_description"),
  twitterImage: logo,
  twitterSite: "@ChronoFoxApp",
});
</script>

<style scoped>
.companies-page {
  width: 100%;
  overflow-x: hidden;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
  padding: 120px 20px 80px;
  position: relative;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 2rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
  opacity: 0.95;
}

.hero-description {
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.hero-cta {
  display: flex;
  gap: 15px;
  margin-bottom: 2rem;
}

.hero-image {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-wrapper {
  position: relative;
  width: 100%;
  transform: perspective(1000px) rotateY(0deg) rotateX(0deg);
  transition: transform 0.6s ease;
}

.image-wrapper:hover {
  transform: perspective(1000px) rotateY(-5deg) rotateX(2deg);
}

.hero-image img {
  width: 100%;
  border-radius: 15px;
  box-shadow:
    0 25px 80px rgba(0, 0, 0, 0.3),
    0 10px 30px rgba(255, 107, 53, 0.2);
}

/* Buttons */
.btn {
  padding: 14px 32px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
  text-align: center;
}

.btn-primary {
  background: white;
  color: #ff6b35;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
  background: transparent;
  color: white;
  border: 2px solid white;
}

.btn-secondary:hover {
  background: white;
  color: #ff6b35;
}

.btn-secondary-outline {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.8);
  color: white;
  backdrop-filter: blur(10px);
}

.btn-secondary-outline:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: white;
}

.btn-large {
  padding: 18px 42px;
  font-size: 1.1rem;
}

/* Pain Points Section */
.pain-points-section {
  padding: 120px 20px;
  background: #f9fafb;
  text-align: center;
}

.pain-points-content {
  max-width: 1200px;
  margin: 0 auto;
}

.pain-points-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 60px;
}

.pain-point-card {
  background: white;
  padding: 40px;
  border-radius: 15px;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
}

.pain-point-card:hover {
  transform: translateY(-5px);
  border-color: #ff6b35;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.pain-point-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 1.8rem;
  color: #dc2626;
}

.pain-point-card h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 15px;
}

.pain-point-card p {
  color: #6b7280;
  line-height: 1.7;
}

/* Solution Section */
.solution-section {
  padding: 120px 20px;
  background: white;
  text-align: center;
}

.solution-content {
  max-width: 1200px;
  margin: 0 auto;
}

.solution-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;
}

.solution-feature {
  padding: 30px 20px;
  text-align: center;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  background: white;
  transition: all 0.3s ease;
  position: relative;
  cursor: pointer;
}

.solution-feature::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.solution-feature:hover::before {
  opacity: 0.05;
}

.solution-feature:hover {
  transform: translateY(-5px);
  border-color: #ff6b35;
  box-shadow: 0 8px 15px rgba(255, 107, 53, 0.15);
}

.solution-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 1.8rem;
  color: white;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.solution-feature:hover .solution-icon {
  transform: scale(1.05) rotate(3deg);
}

.solution-feature h3 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
}

.solution-feature p {
  color: #6b7280;
  line-height: 1.6;
  font-size: 0.95rem;
}

/* Detailed Features Section */
.detailed-features {
  padding: 120px 20px;
  background: #f9fafb;
}

.detailed-features-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.detailed-feature-cards {
  margin: 60px 0 0;
}

.detailed-feature {
  display: grid;
  grid-template-columns: 80px 1fr 1fr;
  gap: 40px;
  align-items: center;
  margin-bottom: 140px;
  text-align: left;
}

.detailed-feature.reverse {
  grid-template-columns: 80px 1fr 1fr;
}

.detailed-feature.reverse .feature-content {
  order: 2;
}

.detailed-feature.reverse .feature-image {
  order: 1;
}

.feature-number {
  font-size: 4rem;
  font-weight: 800;
  color: #e5e7eb;
}

.feature-content h3 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 15px;
}

.feature-content p {
  color: #4b5563;
  line-height: 1.8;
  margin-bottom: 20px;
}

.feature-image img {
  width: 100%;
  border-radius: 15px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

/* Benefits Section */
.benefits-section {
  padding: 120px 20px;
  background: white;
  text-align: center;
}

.benefits-content {
  max-width: 1200px;
  margin: 0 auto;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;
}

.benefit-card {
  padding: 30px 20px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 15px;
  transition: all 0.3s ease;
}

.benefit-card:hover {
  transform: translateY(-5px);
  border-color: #ff6b35;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.benefit-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 1.5rem;
  color: white;
}

.benefit-card h3 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 15px;
}

.benefit-card p {
  color: #6b7280;
  line-height: 1.6;
  font-size: 0.95rem;
}

/* Use Cases Section */
.use-cases-section {
  padding: 120px 20px;
  background: #f9fafb;
  text-align: center;
}

.use-cases-content {
  max-width: 1200px;
  margin: 0 auto;
}

.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  margin-top: 60px;
}

.use-case-card {
  background: white;
  padding: 40px;
  border-radius: 15px;
  border: 2px solid #e5e7eb;
  text-align: left;
  transition: all 0.3s ease;
}

.use-case-card:hover {
  transform: translateY(-5px);
  border-color: #ff6b35;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.use-case-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 1.5rem;
  color: white;
}

.use-case-card h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 15px;
}

.use-case-card p {
  color: #6b7280;
  line-height: 1.7;
}

/* Features Overview Section */
.features-overview {
  padding: 120px 20px;
  background: white;
  text-align: center;
}

/* Pricing Section */
.pricing-section {
  padding: 120px 20px;
  background: #f9fafb;
  text-align: center;
}

/* Enterprise Section */
.enterprise-section {
  padding: 120px 20px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
}

.enterprise-content {
  max-width: 1000px;
  margin: 0 auto;
  text-align: center;
}

.enterprise-text {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.enterprise-text h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: white;
}

.enterprise-text p {
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 1rem;
  opacity: 0.95;
}

.enterprise-features {
  list-style: none;
  padding: 0;
  margin: 1rem auto 1rem;
  text-align: left;
  display: inline-block;
  max-width: 600px;
}

.enterprise-features li {
  padding: 12px 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 15px;
}

.enterprise-features i {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.enterprise-text .btn {
  margin-top: 2rem;
  display: inline-block;
}

/* Trust Section */
.trust-section {
  padding: 120px 20px;
  background: white;
  text-align: center;
}

.trust-features {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 60px auto 0;
}

.trust-item {
  padding: 30px;
}

.trust-item i {
  font-size: 3rem;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
}

.trust-item h4 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
}

.trust-item p {
  color: #6b7280;
  font-size: 1rem;
}

/* Final CTA Section */
.final-cta {
  padding: 100px 20px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
  text-align: center;
}

.final-cta h2 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
}

.final-cta p {
  font-size: 1.3rem;
  margin-bottom: 2.5rem;
  opacity: 0.9;
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 2rem;
}

.cta-note {
  font-size: 1rem;
  opacity: 0.95;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 25px;
  flex-wrap: wrap;
}

.cta-note-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.cta-note-item i {
  flex-shrink: 0;
}

/* Common Styles */
.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #6b7280;
  margin-bottom: 4rem;
  margin-top: 1rem;
}

.section-description {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 2rem;
}

.gradient-text {
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  margin: 0 auto 1rem;
  border-radius: 2px;
}

/* Responsive Design */
@media (max-width: 1050px) {
  .hero-image {
    display: none;
  }

  .hero-content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1024px) {
  .solution-grid,
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .pain-points-grid,
  .trust-features {
    grid-template-columns: repeat(2, 1fr);
  }

  .detailed-feature,
  .detailed-feature.reverse {
    grid-template-columns: 1fr;
  }

  .feature-number {
    font-size: 3rem;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.5rem;
  }

  .pain-points-grid,
  .solution-grid,
  .benefits-grid,
  .use-cases-grid,
  .trust-features {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 2rem;
  }

  .hero-cta,
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }

  .btn {
    width: 100%;
    max-width: 300px;
    text-align: center;
  }

  .final-cta h2 {
    font-size: 2rem;
  }
}
</style>
